import { useRef, useState } from "react";
import { Upload, FileCheck2, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function FileUploadField({ docType, label, submissionId, fileRecord, onChange, required = false, testId, note, otherFiles = [], compact = false, hideLabel = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File troppo grande (max 15 MB)");
      return;
    }
    if (file.type !== "application/pdf") {
      toast.error("Sono ammessi solo file PDF");
      return;
    }
    if (!submissionId) {
      toast.error("Crea prima una richiesta, compila lo step 2");
      return;
    }
    // Duplicate detection: same filename + same size in another slot
    const dup = (otherFiles || []).find(
      (f) => f.doc_type !== docType && f.filename === file.name && Number(f.size) === Number(file.size)
    );
    if (dup) {
      toast.error(`Questo file sembra già caricato come "${dup.doc_type}". Ogni documento deve essere diverso.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      const r = await api.post(`/submissions/${submissionId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data);
      toast.success(`${label} caricato`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!fileRecord) return;
    try {
      await api.delete(`/submissions/${submissionId}/files/${fileRecord.id}`);
      onChange(null);
      toast.success("File rimosso");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={compact ? "space-y-0" : "space-y-1.5"} data-testid={`${testId}-wrapper`}>
      {!hideLabel && (
        <div className="text-sm font-medium text-slate-700">
          {label} {required && <span className="text-red-700">*</span>}
        </div>
      )}

      {fileRecord ? (
        <div
          className={
            compact
              ? "flex items-center justify-between gap-3 border border-emerald-300 bg-emerald-50 rounded-md px-3 py-2"
              : "flex items-center justify-between gap-3 border border-green-800/30 bg-green-50 rounded-md px-4 py-3"
          }
          data-testid={`${testId}-filled`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <FileCheck2 size={compact ? 16 : 18} className="text-emerald-700 shrink-0" />
            <div className="min-w-0">
              <div className={`${compact ? "text-xs" : "text-sm"} font-medium text-slate-900 truncate`}>{fileRecord.filename}</div>
              <div className="text-[11px] text-slate-500">{(fileRecord.size / 1024).toFixed(1)} KB{fileRecord.generated ? " · generato" : ""}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="text-slate-500 hover:text-red-700 transition-colors p-1 shrink-0"
            data-testid={`${testId}-remove`}
            aria-label="Rimuovi file"
          >
            <X size={compact ? 14 : 16} />
          </button>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files[0]);
          }}
          className={
            compact
              ? `flex items-center justify-center gap-2 border border-dashed rounded-md cursor-pointer transition-colors px-3 py-2.5 text-xs ${
                  dragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                }`
              : `flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg cursor-pointer transition-colors px-6 py-6 ${
                  dragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                }`
          }
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <Loader2 size={compact ? 14 : 20} className="text-slate-500 animate-spin" />
          ) : (
            <Upload size={compact ? 14 : 20} className="text-slate-500" />
          )}
          <span className={`${compact ? "text-xs" : "text-sm"} text-slate-700 font-medium`}>
            {uploading ? "Caricamento..." : compact ? "Carica PDF" : "Trascina qui o clicca per caricare"}
          </span>
          {!compact && <span className="text-xs text-slate-500">PDF · max 15 MB</span>}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid={`${testId}-input`}
          />
        </label>
      )}
      {note && !compact && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1.5" data-testid={`${testId}-note`}>
          {note}
        </p>
      )}
    </div>
  );
}
